import { useRef, useCallback, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mic, Upload, X, FileAudio, CheckCircle, AlertTriangle } from "lucide-react";

// Native formats (work without server-side conversion)
const NATIVE_FORMATS = ['.wav', '.flac', '.ogg'];
// All accepted formats (some may need server-side conversion)
const ACCEPTED = ".wav,.flac,.ogg,.mp3,.m4a,.mp4,.mov,.aac,.webm,.avi,.3gp,.mkv,audio/*,video/*";

// Formats that need conversion (may not work in all deployments)
const CONVERSION_FORMATS = ['mp3', 'mp4', 'm4a', 'mov', 'aac', 'avi', 'webm', '3gp', 'mkv'];

export default function AudioUploader({ audioFile, setAudioFile, disabled }) {
  const inputRef = useRef(null);
  const [formatWarning, setFormatWarning] = useState(null);

  const checkFileFormat = (file) => {
    if (!file) return;
    const ext = file.name.toLowerCase().split('.').pop();
    
    if (CONVERSION_FORMATS.includes(ext)) {
      setFormatWarning(
        `Note: .${ext} files may not work in all environments. ` +
        `If you encounter errors, please convert to WAV format using cloudconvert.com or convertio.co`
      );
    } else {
      setFormatWarning(null);
    }
    setAudioFile(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    if (disabled) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) checkFileFormat(file);
  }, [setAudioFile, disabled]);

  const handleSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) checkFileFormat(file);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <Card className="border border-slate-200 shadow-none rounded-sm overflow-hidden h-full">
      <div className="border-b border-slate-200 p-4 bg-slate-50/50 flex items-center gap-2">
        <Mic size={18} className="text-slate-500" />
        <span className="text-sm font-bold uppercase tracking-wider text-slate-500">Engine Audio</span>
      </div>
      <CardContent className="p-5">
        {!audioFile ? (
          <div
            className={`border-2 border-dashed rounded-sm p-8 text-center transition-all duration-200 ${
              disabled 
                ? "border-slate-200 bg-slate-50 cursor-not-allowed opacity-60"
                : "border-slate-300 cursor-pointer hover:border-slate-400 hover:bg-slate-50/50"
            }`}
            onClick={() => !disabled && inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            data-testid="audio-dropzone"
          >
            <Upload size={36} className={`mx-auto mb-4 ${disabled ? "text-slate-300" : "text-slate-400"}`} />
            <p className={`text-base font-medium ${disabled ? "text-slate-400" : "text-slate-600"}`}>
              {disabled ? "Processing audio..." : "Drop audio file here"}
            </p>
            <p className={`text-sm mt-2 ${disabled ? "text-slate-300" : "text-slate-400"}`}>
              {disabled ? "Please wait" : "or click to browse"}
            </p>
            <div className="mt-4 p-3 bg-emerald-50 rounded-sm border border-emerald-200">
              <p className="text-sm text-emerald-700 font-bold uppercase tracking-wider">
                ✓ Best: WAV, FLAC, OGG
              </p>
              <p className="text-sm text-emerald-600 mt-1">
                These formats work in all environments
              </p>
            </div>
            <p className="text-sm text-slate-400 mt-2">
              MP3, MP4, M4A may work if server supports conversion
            </p>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPTED}
              onChange={handleSelect}
              className="hidden"
              disabled={disabled}
              data-testid="audio-input"
            />
          </div>
        ) : (
          <div className="space-y-4" data-testid="audio-preview">
            <div className="flex items-center gap-4 p-4 bg-emerald-50 border border-emerald-200 rounded-sm">
              <div className="w-12 h-12 bg-emerald-100 rounded-sm flex items-center justify-center flex-shrink-0">
                <FileAudio size={22} className="text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-medium text-slate-800 truncate">{audioFile.name}</p>
                <p className="text-sm text-slate-500 font-mono">{formatSize(audioFile.size)}</p>
              </div>
              <CheckCircle size={20} className="text-emerald-500 flex-shrink-0" />
            </div>

            {formatWarning && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-sm">
                <AlertTriangle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-700">{formatWarning}</p>
              </div>
            )}

            <audio controls className="w-full h-12" data-testid="audio-player">
              <source src={URL.createObjectURL(audioFile)} />
            </audio>

            <Button
              variant="outline"
              size="sm"
              className="w-full uppercase tracking-wider font-bold text-sm rounded-sm py-2.5"
              onClick={() => {
                setAudioFile(null);
                setFormatWarning(null);
              }}
              disabled={disabled}
              data-testid="remove-audio-btn"
            >
              <X size={14} className="mr-2" /> Remove
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
